import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, Checkbox, CircularProgress, FormControlLabel, IconButton, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CommonDashboardBox } from "../../components/Dashboard/CommonDashboardBox";
import { PushReportSchedule } from "../../components/PushReportSchedule/PushReportSchedule";
import RoleSelector from "../../components/rolesSelector/rolesSelector";
import { SystemFlatSwitch } from "../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { type SystemChipItem, SystemChips } from "../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { DateRangeSelector } from "../../components/UIToolkit/molecules/DateRangeSelector";
import { JobSelector } from "../../components/UIToolkit/molecules/JobSelector";
import { OneTimeNoticePopover } from "../../components/UIToolkit/molecules/OneTimeNoticePopover";
import { SystemReactTinyPopover } from "../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useAuth } from "../../contexts/AuthProvider";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useDepartmentJobs } from "../../hooks/useDepartmentJobs";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useRevealWage } from "../../hooks/useRevealWage";
import { useSaveAsDefaultEnabled } from "../../hooks/useSaveAsDefaultEnabled";
import { formatWeekRangeWithMonth } from "../../utils/dateFormatting";
import { type NurseFullRole, type OtMode, type PushReportType, StatsMode } from "../../utils/models";
import { ManagementCenterSettingsMenu } from "./ManagementCenterSettingsMenu";
import { ShiftHoursSelect } from "./ShiftHoursSelect";
import type { ManagementCenterView } from "./useManagementCenterView";
import type { ShiftHourOption } from "./useShiftHourScope";
import { getLatestAvailableDate } from "./utils";

// DEFAULT: keep the Shift Hours control visible before its first options request resolves.
const EMPTY_SHIFT_HOUR_OPTIONS: ShiftHourOption[] = [];

/**
 * APP-731 rollout window — one-off campaign boundaries, NOT tunables.
 *
 * "Estimate missing wage" flipped ON by default and existing users were backfilled to ON, which
 * silently moved cost figures people had already read. These two dates bound who gets told, and for
 * how long, so the notice cannot outlive the news:
 *  - `shippedBefore` — accounts created after 2026-09-23 joined once the new behavior was already
 *    the behavior, so nothing changed for them and the notice is not theirs. `endOf("day")` because
 *    the rule is "created after that *day*", so someone who signed up on the 23rd still qualifies.
 *  - `expiresAt` — after 2026-11-01 this is no longer news for anyone; the notice stops rendering
 *    whether or not it was ever acknowledged.
 *
 * Both are fixed in UTC rather than the viewer's zone so the campaign opens and closes at the same
 * instant for every user. Delete this block, the notice, and its i18n keys once the window closes.
 */
const APP_731_ESTIMATE_MISSING_WAGE_NOTICE = {
	key: "app-731-estimate-missing-wage",
	shippedBefore: DateTime.fromISO("2026-09-23", { zone: "utc" }).endOf("day"),
	expiresAt: DateTime.fromISO("2026-11-01", { zone: "utc" }).endOf("day"),
} as const;

const getTimeChipsItems = (): SystemChipItem<number>[] => [
	{
		label: "Quarter",
		value: 120,
	},
	{
		label: "Month",
		value: 30,
	},
	{
		label: "2 Weeks",
		value: 14,
	},
];

type Metric = "ppd" | "hppd";
export type Grouping = "daily" | "weekly";

type Props = {
	onStartDateChange: (date: DateTime) => void;
	onEndDateChange: (date?: DateTime) => void;
	isActual?: boolean;
	onActualToggleChange?: (isActualShown: boolean) => void;
	onRolesChange: (roles: NurseFullRole[]) => void;
	selectedRoles: NurseFullRole[];
	/** The roles the role filter offers — the shown department's, which is the drilled one in the ops backoffice. */
	roleOptions: NurseFullRole[];
	/** The drilled department in the ops backoffice; undefined when the page shows the chosen one. */
	departmentId?: string;
	// INH-3407: parallel job filter; the header conditionally renders JobSelector when
	// job_management_enabled is on for the department.
	selectedJobs?: string[];
	onJobsChange?: (jobs: string[]) => void;
	showActualToggle?: boolean;
	metric?: Metric;
	onMetricToggle?: (metric: Metric) => void;
	grouping?: Grouping;
	onGroupingToggle?: (grouping: Grouping) => void;
	shiftHourOptions?: ShiftHourOption[];
	shiftHoursUpdating?: boolean;
	shiftHoursUnavailable?: boolean;
	selectedShiftHours?: string[];
	onShiftHoursChange?: (shiftHours: string[]) => void;
	onDownload?: () => Promise<void>;
	isDownloading?: boolean;
	pushReportType?: PushReportType;
	laborCostEnabled?: boolean;
	onLaborCostToggle?: (enabled: boolean) => void;
	includeHolidays?: boolean;
	onIncludeHolidaysToggle?: (enabled: boolean) => void;
	includeLateScheduled?: boolean;
	onIncludeLateScheduledToggle?: (enabled: boolean) => void;
	// Required (not optional): forces every page rendering the header to wire the toggle through to
	// the settings menu — a missing page fails to compile instead of silently hiding the control.
	estimateMissingWage: boolean;
	onEstimateMissingWageToggle: (enabled: boolean) => void;
	/** APP-731 rollout notice acknowledged. Required for the same reason as the toggle above: every page
	 *  rendering this header must wire the event through, instead of silently dropping the tracking. */
	onEstimateMissingWageNoticeDismissed: () => void;
	wantedMode?: StatsMode;
	onWantedModeToggle?: (enabled: boolean) => void;
	/** Weekly-OT attribution mode (Trigger vs Actual). When provided, renders the centralized settings menu. */
	otMode?: OtMode;
	onOtModeChange?: (mode: OtMode) => void;
	/** Centralized settings menu actions (align preset / save-as-default / restore). */
	onAlignToDayView?: () => void;
	onSaveMcDefaults?: () => void | Promise<void>;
	onRestoreMcSettings?: () => void | Promise<void>;
	/** Default chip value in days (defaults to 30 = Month) */
	defaultDays?: number;
	/** Hide the timeframe selection (date display, time chips, custom range picker) */
	hideTimeframe?: boolean;
	/**
	 * Which Management Center body the header fronts. "multi" reduces the header to the portfolio-
	 * relevant controls only (timeframe, actual/scheduled, view toggle); omitted/"single" renders
	 * the full single-facility filter set unchanged.
	 */
	mode?: ManagementCenterView;
	/**
	 * Single/Multi view switcher. The toggle renders only when this is provided (the page withholds
	 * it when embedded) AND the user can access more than one facility.
	 */
	onModeChange?: (view: ManagementCenterView) => void;
};

export const ManagementCenterHeader: FC<Props> = ({
	onStartDateChange,
	onEndDateChange,
	isActual,
	onActualToggleChange,
	onRolesChange,
	selectedRoles,
	roleOptions,
	departmentId,
	selectedJobs,
	onJobsChange,
	showActualToggle = true,
	metric,
	onMetricToggle,
	grouping,
	onGroupingToggle,
	shiftHourOptions,
	shiftHoursUpdating,
	shiftHoursUnavailable,
	selectedShiftHours,
	onShiftHoursChange,
	onDownload,
	isDownloading,
	pushReportType,
	laborCostEnabled,
	onLaborCostToggle,
	includeHolidays,
	onIncludeHolidaysToggle,
	includeLateScheduled,
	onIncludeLateScheduledToggle,
	estimateMissingWage,
	onEstimateMissingWageToggle,
	onEstimateMissingWageNoticeDismissed,
	wantedMode,
	onWantedModeToggle,
	otMode,
	onOtModeChange,
	onAlignToDayView,
	onSaveMcDefaults,
	onRestoreMcSettings,
	defaultDays = 30,
	hideTimeframe = false,
	mode,
	onModeChange,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const canViewWage = useRevealWage();
	const { userInfo } = useAuth();
	const isMultiMode = mode === "multi";
	const showViewToggle = onModeChange !== undefined && userInfo.departments.length > 1;
	const departmentFromQuery = searchParams.get("department");
	const { chosenDepartment } = useChosenDepartment();
	const { isAnyDemo } = useDepartmentFlags();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const canSaveAsDefault = useSaveAsDefaultEnabled();
	// DEFAULT: every page but the ops backoffice drill-down shows the chosen department.
	const departmentJobs = useDepartmentJobs(departmentId ?? chosenDepartment.department_id);
	const timeChipsItems = getTimeChipsItems();
	const currentDate = useMemo(() => DateTime.now().setZone(chosenDepartment.timezone), [chosenDepartment.timezone]);
	// Today's data is incomplete (shifts in progress, late actuals, unfinalized census), so the
	// Management Center treats the day before yesterday as the latest fully-available day (yesterday
	// is still settling: late punches, corrections, overnight rollups). See INH-3519 and
	// MC_LATEST_AVAILABLE_DAY_OFFSET.
	// Shared with useManagementCenterFilters' default fallback so the header and the hook can't drift
	// a day apart (which fired two staff-mix/cost queries with different start dates).
	const latestAvailableDate = useMemo(() => getLatestAvailableDate(chosenDepartment.timezone), [chosenDepartment.timezone]);

	const urlStartDate = searchParams.get("startDate");
	const urlEndDate = searchParams.get("endDate");

	const defaultChip = useMemo(() => timeChipsItems.find((c) => c.value === defaultDays) ?? timeChipsItems[1], [timeChipsItems, defaultDays]);
	const [selectedTimeChip, setSelectedTimeChip] = useState<SystemChipItem<number>>(() => {
		if (urlStartDate && !urlEndDate) {
			const start = DateTime.fromISO(urlStartDate);
			if (start.isValid) {
				const daysDiff = Math.round(currentDate.diff(start, "days").days);
				const match = timeChipsItems.find((c) => Math.abs(c.value - daysDiff) <= 1);
				if (match) return match;
			}
		}
		return defaultChip;
	});
	const [isCustomRangeEnabled, setIsCustomRangeEnabled] = useState(() => !!(urlStartDate && urlEndDate));
	const [customStartDate, setCustomStartDate] = useState<DateTime>(() => {
		if (urlStartDate) {
			const parsed = DateTime.fromISO(urlStartDate);
			if (parsed.isValid) return parsed > latestAvailableDate ? latestAvailableDate : parsed;
		}
		return latestAvailableDate.minus({ days: 30 });
	});
	const [customEndDate, setCustomEndDate] = useState<DateTime>(() => {
		if (urlEndDate) {
			const parsed = DateTime.fromISO(urlEndDate);
			if (parsed.isValid) return parsed > latestAvailableDate ? latestAvailableDate : parsed;
		}
		return latestAvailableDate;
	});
	const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);

	const changeTimeChip = useCallback((value: SystemChipItem<number>) => {
		setSelectedTimeChip(value);
		setIsCustomRangeEnabled(false);
	}, []);

	const handleCustomRangeEnabledChange = useCallback(
		(enabled: boolean) => {
			setIsCustomRangeEnabled(enabled);
			if (enabled) {
				setSelectedTimeChip({ label: "", value: 0, disabled: false }); // Clear chip selection
			} else {
				setSelectedTimeChip(defaultChip); // Reset to default chip
			}
		},
		[timeChipsItems],
	);

	const handleDateRangeClickOutside = useCallback((e: MouseEvent) => {
		// Don't close if the click landed inside a MUI date picker popper (rendered in a portal)
		const target = e.target as HTMLElement;
		if (target.closest(".MuiPickersPopper-root, .MuiDateCalendar-root")) return;
		setIsDateRangeOpen(false);
	}, []);

	const closeDateRange = useCallback(() => setIsDateRangeOpen(false), []);
	const toggleDateRange = useCallback(() => setIsDateRangeOpen((prev) => !prev), []);

	const handleCustomStartDateChange = useCallback(
		(date: DateTime) => {
			setCustomStartDate(date);
			handleCustomRangeEnabledChange(true);
		},
		[handleCustomRangeEnabledChange],
	);

	const handleCustomEndDateChange = useCallback(
		(date: DateTime) => {
			setCustomEndDate(date);
			handleCustomRangeEnabledChange(true);
			setIsDateRangeOpen(false);
		},
		[handleCustomRangeEnabledChange],
	);

	const selectedEndDate = useMemo(() => latestAvailableDate, [latestAvailableDate]);
	const selectedDateFrame = useMemo(() => selectedEndDate.minus({ days: selectedTimeChip.value }), [selectedEndDate, selectedTimeChip]);

	useEffect(() => {
		const startDate = isCustomRangeEnabled ? customStartDate : selectedDateFrame;
		const endDate = isCustomRangeEnabled ? customEndDate : selectedEndDate;
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.set("startDate", startDate.toISODate() ?? "");
				if (isCustomRangeEnabled && endDate) {
					next.set("endDate", endDate.toISODate() ?? "");
				} else {
					next.delete("endDate");
				}
				return next;
			},
			{ replace: true },
		);
		onStartDateChange(startDate);
		onEndDateChange(endDate);
	}, [isCustomRangeEnabled, customStartDate, customEndDate, selectedDateFrame, selectedEndDate, setSearchParams]);

	return (
		<CommonDashboardBox
			sx={{
				width: "100%",
				height: "fit-content",
				minHeight: "74px",
				display: "flex",
				justifyContent: "start",
				alignItems: "center",
				gap: 1.5,
				// Multi mode's reduced control set wraps on narrow screens so the view toggle stays
				// reachable; the single-mode row is left untouched (nowrap, its long-standing layout).
				flexWrap: isMultiMode ? "wrap" : "nowrap",
			}}
		>
			{!hideTimeframe && (
				<>
					{departmentFromQuery && (
						<Typography
							sx={{
								fontWeight: 700,
								fontSize: { xl: "24px", xs: "18px" },
								lineHeight: "100%",
								color: "primary.dark",
								marginRight: 2,
							}}
						>
							{departmentFromQuery}
						</Typography>
					)}
					<Typography
						sx={{
							fontWeight: 700,
							fontSize: { xl: "24px", xs: "18px" },
							lineHeight: "100%",
							color: "primary.dark",
							width: { xl: "200px", xs: "180px" },
						}}
					>
						{isCustomRangeEnabled ? formatWeekRangeWithMonth(customStartDate, customEndDate) : formatWeekRangeWithMonth(selectedDateFrame, selectedEndDate)}
					</Typography>
					<SystemChips
						data-testid="management-center-time-chips"
						variant={"filled-no-border"}
						forceFitContent
						items={timeChipsItems}
						onChange={changeTimeChip}
						selectedItems={isCustomRangeEnabled ? [] : [selectedTimeChip]}
					/>
					<SystemReactTinyPopover
						isOpen={isDateRangeOpen}
						onClose={closeDateRange}
						onClickOutside={handleDateRangeClickOutside}
						positions={["bottom"]}
						align="start"
						content={
							<DateRangeSelector
								startDate={customStartDate}
								endDate={customEndDate}
								onStartDateChange={handleCustomStartDateChange}
								onEndDateChange={handleCustomEndDateChange}
								maxDate={latestAvailableDate}
								removeBackground
								removeBorder
								padding="0px"
							/>
						}
					>
						<Tooltip title={t("custom.range")}>
							<IconButton
								onClick={toggleDateRange}
								data-testid="management-center-date-range-toggle"
								sx={{
									color: isCustomRangeEnabled ? "primary.main" : "var(--inhouse-primary-dark)",
								}}
							>
								<CalendarMonthOutlinedIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</SystemReactTinyPopover>
				</>
			)}
			{!isAnyDemo &&
				!isMultiMode &&
				(jobManagementEnabled && departmentJobs.length > 0 && onJobsChange ? (
					<JobSelector
						variant="dropdown"
						jobs={departmentJobs}
						staffCountDepartmentId={departmentId}
						selectedJobIds={selectedJobs ?? []}
						onSelectedJobsChanged={onJobsChange}
						label={t("job")}
						formControlSx={{ width: { xl: "220px", xs: "120px" } }}
					/>
				) : (
					<RoleSelector
						width={{
							xl: "220px",
							xs: "120px",
						}}
						onRolesChange={onRolesChange}
						chosenDepartment={chosenDepartment}
						roles={roleOptions}
						selectedRoles={selectedRoles}
					/>
				))}
			{onShiftHoursChange && (shiftHoursUpdating || shiftHoursUnavailable || (shiftHourOptions && shiftHourOptions.length > 0)) && !isMultiMode && (
				<ShiftHoursSelect
					width={{
						xl: "220px",
						xs: "160px",
					}}
					shiftHourOptions={shiftHourOptions ?? EMPTY_SHIFT_HOUR_OPTIONS}
					updating={!!shiftHoursUpdating}
					unavailable={!!shiftHoursUnavailable}
					selectedShiftHours={selectedShiftHours ?? []}
					onShiftHoursChange={onShiftHoursChange}
				/>
			)}
			{onWantedModeToggle && !isMultiMode && (
				<FormControlLabel
					control={<Checkbox checked={wantedMode === StatsMode.PPD_HPPD} onChange={(_, checked) => onWantedModeToggle(checked)} size="small" />}
					label={t("use.hppd.target")}
					sx={{
						m: 0,
						"& .MuiFormControlLabel-label": {
							fontSize: {
								xl: "14px",
								xs: "10px",
							},
							color: "primary.dark",
						},
					}}
				/>
			)}
			{metric && onMetricToggle && canViewWage && !isMultiMode && (
				<SystemFlatSwitch
					options={[
						{ label: "HPPD", value: "hppd" as Metric },
						{ label: "PPD", value: "ppd" as Metric },
					]}
					activeIndexOrValue={metric}
					onSwitch={(option) => onMetricToggle(option.value as Metric)}
				/>
			)}
			{grouping && onGroupingToggle && !isMultiMode && (
				<SystemFlatSwitch
					options={[
						{ label: t("daily"), value: "daily" as Grouping },
						{ label: t("weekly"), value: "weekly" as Grouping },
					]}
					activeIndexOrValue={grouping}
					onSwitch={(option) => onGroupingToggle(option.value as Grouping)}
				/>
			)}
			{showActualToggle && onActualToggleChange && (
				<SystemFlatSwitch
					options={[
						{
							label: t("scheduled"),
							value: t("scheduled"),
						},
						{
							label: t("actual"),
							value: t("actual"),
						},
					]}
					activeIndexOrValue={isActual ? 1 : 0}
					onSwitch={(_, activeIndex) => onActualToggleChange(activeIndex === 1)}
				/>
			)}
			{showViewToggle && onModeChange && (
				<SystemFlatSwitch<ManagementCenterView>
					variant="outline"
					data-testid="management-center-view-toggle"
					options={[
						{ label: t("single.facility"), value: "single", "data-testid": "management-center-view-toggle-single" },
						{ label: t("all.facilities"), value: "multi", "data-testid": "management-center-view-toggle-multi" },
					]}
					activeIndexOrValue={isMultiMode ? "multi" : "single"}
					onSwitch={(option) => onModeChange(option.value)}
				/>
			)}
			{otMode && onOtModeChange && onAlignToDayView && onSaveMcDefaults && onRestoreMcSettings && !isMultiMode && (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.5,
						flex: 1,
						justifyContent: "end",
					}}
				>
					{/* Wage-gated exactly like the toggle it is about: a user who cannot see "Estimate missing
					    wage" must not be told it changed, and must never get a marker with no off switch behind
					    it. The popover owns the rest of the gating (acknowledged / too new / expired) and
					    renders nothing — no icon, no gap — the moment any of it says no. */}
					{canViewWage && (
						<OneTimeNoticePopover
							noticeKey={APP_731_ESTIMATE_MISSING_WAGE_NOTICE.key}
							expiresAt={APP_731_ESTIMATE_MISSING_WAGE_NOTICE.expiresAt}
							onlyForAccountsCreatedBefore={APP_731_ESTIMATE_MISSING_WAGE_NOTICE.shippedBefore}
							onDismiss={onEstimateMissingWageNoticeDismissed}
							dismissLabel={t("estimate.missing.wage.notice.dismiss")}
							triggerLabel={t("estimate.missing.wage.notice.trigger")}
							// `warning`, not the raw `COLORS.ORANGE`: the marker is saying "a number you rely on
							// moved", which is a status, and the status palette is what re-tints with the brand.
							tone="warning"
							noticeIcon={<CampaignOutlinedIcon fontSize="inherit" aria-hidden />}
							dataTestId="mc-estimate-missing-wage-notice"
						>
							{t("estimate.missing.wage.notice")}
						</OneTimeNoticePopover>
					)}
					<ManagementCenterSettingsMenu
						laborCostEnabled={canViewWage ? laborCostEnabled : undefined}
						onLaborCostToggle={canViewWage ? onLaborCostToggle : undefined}
						includeHolidays={includeHolidays}
						onIncludeHolidaysToggle={onIncludeHolidaysToggle}
						includeLateScheduled={includeLateScheduled}
						onIncludeLateScheduledToggle={onIncludeLateScheduledToggle}
						isActual={isActual}
						estimateMissingWage={canViewWage ? estimateMissingWage : undefined}
						onEstimateMissingWageToggle={canViewWage ? onEstimateMissingWageToggle : undefined}
						otMode={otMode}
						onOtModeChange={onOtModeChange}
						onAlignToDayView={onAlignToDayView}
						canSaveAsDefault={canSaveAsDefault}
						onSaveAsDefault={onSaveMcDefaults}
						onRestore={onRestoreMcSettings}
					/>
				</Box>
			)}
			{(pushReportType || onDownload) && !isMultiMode && (
				<Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
					{pushReportType && chosenDepartment?.department_id && (
						<PushReportSchedule departmentId={chosenDepartment.department_id} reportType={pushReportType} />
					)}
					{onDownload && (
						<Tooltip title={t("download")}>
							<IconButton onClick={onDownload} disabled={isDownloading}>
								{isDownloading ? <CircularProgress size={20} /> : <FileDownloadOutlinedIcon />}
							</IconButton>
						</Tooltip>
					)}
				</Box>
			)}
			{isAnyDemo && !isMultiMode && (
				<SystemButton buttonVariant="secondary" onClick={() => navigate("/management-center-multi")} sx={{ marginLeft: "auto" }}>
					Network
				</SystemButton>
			)}
		</CommonDashboardBox>
	);
};
